"use client";

import { useEffect } from 'react';

export function GlobalErrorHandler() {
  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Check if it's an external script error that we should ignore
      const errorMessage = event.reason?.message || event.reason?.toString() || '';
      const errorStack = event.reason?.stack || '';
      
      if (isExternalScriptError(errorMessage, errorStack)) {
        console.warn('External script error detected - likely from browser extension or third-party service');
        // Prevent the error from showing in console and affecting the app
        event.preventDefault();
        return;
      }
      
      // For other errors, you might want to report them
      // Example: Sentry.captureException(event.reason);
    };

    // Handle general JavaScript errors
    const handleError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);
      
      // Check if it's an external script error that we should ignore
      const errorMessage = event.message || '';
      const errorStack = event.error?.stack || '';
      
      if (isExternalScriptError(errorMessage, errorStack)) {
        console.warn('External script error detected - likely from browser extension or third-party service');
        // Prevent the error from showing in console and affecting the app
        event.preventDefault();
        return;
      }
    };

    // Function to detect external script errors
    const isExternalScriptError = (message: string, stack: string): boolean => {
      const externalErrorPatterns = [
        'enabledFeatures',
        'args.site',
        'isFeatureBroken',
        'updateFeaturesInner',
        'chrome-extension://',
        'moz-extension://',
        'safari-extension://',
        'extension://',
        'browser-extension://',
        'web-accessible-resources',
        'content_script',
        'background_script',
        'injected_script',
        'anonymous code',
        'overrideMethod',
        'geckoformboundary',
        'firefox',
        'browser extension',
        'third-party script',
        'external script'
      ];
      
      const combinedText = `${message} ${stack}`.toLowerCase();
      
      return externalErrorPatterns.some(pattern => 
        combinedText.includes(pattern.toLowerCase())
      );
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return null; // This component doesn't render anything
}
